/*
 * One-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * QuestionsScreen.js - Show the questions.
*/

import React from 'react';
import { Audio } from 'expo-av';
import PropTypes from 'prop-types';
import SubitiseArray from './SubitiseArray';
import SubitisingGenerator from './SubitisingGenerator';
import * as colours from '../../colours';
import { TopicCategoryName } from '../../categories';
import { dice_formations } from './DiceFormations';
import useSettingsStore from '../../hooks/useSettingsStore';

export default class Subitising extends React.Component {

  constructor(route, navigation) {
    super();
    this._mounted = false;
    this._timer = null;
    this.route = route;
    this.navigation = navigation;
    this.name = this.route.params.name;
    this.color = this.route.params.color;
    this.audioEnabled = useSettingsStore.getState().audioEnabled; // Doesn't trigger rerenders
    this.state = {
      answer: '',
      userAnswer: '',
      formation: dice_formations[0],
      hint: '',
      hintVisible: false,
      timeout: 0,
      subHidden: false,
      subTimer: null,
      colour: colours.red,
      disabledButtons: false,
    };
    this.score = 0;
    this.subitisingGenerator = new SubitisingGenerator();
    this.previousQuestions = [];

    // Bind callback functions
    this.handleInputEvent = this.handleInputEvent.bind(this);
    this.toggleHint = this.toggleHint.bind(this);
    this.playSound = this.playSound.bind(this);
    this.setSubTimer = this.setSubTimer.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    // Use the focus listener as play again button
    // doesn't recall componentDidMouse
    this._sub = this.navigation.addListener(
      'focus',
      this._componentFocused
    );
  }

  reset() {
    this.setState({ userAnswer: '' });
    this.score = 0;
    this.previousQuestions = [];
    this.subitisingGenerator.initPreviousQuestions();
  }

  _componentFocused = () => {
    // Set Question Timer
    this._mounted = true;
    this._timer = setTimeout(
      () => {
        clearTimeout(this.state.subTimer);
        this.setState({ subTimer: null });

        this._mounted = false;
        this._timer = null;
        if (this.previousQuestions.length > 1) {
          if (this.previousQuestions[this.previousQuestions.length - 1].correct === undefined) {
            this.previousQuestions.pop();
          }
        }
        this.navigation.replace('Results', {
          score: this.score,
          category: {
            name: this.name,
            color: this.color
          },
          results: this.previousQuestions,
          type: TopicCategoryName.SUBITISING
        });
      },
      60000
    );

    this.reset();
    this.getQuestion(this.name);
    this.correctSound = new Audio.Sound();
    this.correctSound.loadAsync(require('../../assets/correct.m4a'));

    this.wrongSound = new Audio.Sound();
    this.wrongSound.loadAsync(require('../../assets/wrong.m4a'));
    
    this.setState(
      { 
        timer: this._timer,
      }
    );
  };

  async playSound(correct) {
    if (correct) {
      if(this.correctSound) {
        this.correctSound.replayAsync().catch((error) => { });
      }
    } else {
      if (this.wrongSound){
        this.wrongSound.replayAsync().catch((error) => { });
      }
    }
  }

  async componentWillUnmount() {
    this._mounted = false;
    // Cancel the timer going to the results page
    clearTimeout(this.state.timer);
    clearTimeout(this.state.subTimer);
    this.setState({ subTimer: null });

    if(this.correctSound) {
      await this.correctSound.stopAsync();
      await this.correctSound.unloadAsync();
    }

    if(this.wrongSound) {
      await this.wrongSound.stopAsync();
      await this.wrongSound.unloadAsync();
    }
  }

  getSubitiseArray() {
    /* Subitise array is a 5 x 5 array of cirlces */
    const subitiseComponent = <SubitiseArray data={this.state} hide={this.state.subHidden} category={this.name} />;
    return subitiseComponent;
  }

  formatQuestion(question_type) {
    const question = `${question_type}`;
    return question;
  }

  setSubTimer(timeout) {
    let subTimer;
    const subHidden = false;
    if (this.state.subTimer == null) {
      if (timeout > 0) {
        subTimer = setTimeout(
          () => {
            this.setState({ subHidden: true });
            clearTimeout(this.state.subTimer);
            this.setState({ subTimer: null });
          },
          timeout
        );
      }

      this.setState({
        subHidden,
        subTimer,
      });
    }
    return { subTimer, subHidden };

  }

  getQuestion(questionType) {

    const {
      answer, formation, timeout, colour, colour2, object, questionSelection
    } = this.subitisingGenerator.generateQuestion(questionType, this.previousQuestions);
    const question = this.formatQuestion(questionSelection, answer);

    if (this._mounted && this._timer != null) {
      this.previousQuestions.push({ question, answer, timeout });
      this.setSubTimer(timeout);
      this.setState(
        {
          answer,
          question,
          formation,
          hint: null,
          timeout,
          colour,
          colour2,
          object,
          category: questionSelection,
          disabledButtons: false,
        }
      );
    }
  }

  async handleInputEvent(event) {

    // Disable the buttons
    this.setState({ disabledButtons: true });

    this.setState({ userAnswer: '' });

    // Clear sub timer
    clearTimeout(this.state.subTimer);
    this.setState({ subTimer: null });

    // Check answer is correct
    const userAnswer = `${this.state.userAnswer}${event}`;
    const correct = parseInt(userAnswer) == this.previousQuestions[this.previousQuestions.length - 1].answer;

    // Add user answer and correctness to list
    this.previousQuestions[this.previousQuestions.length - 1].userAnswer = userAnswer;
    this.previousQuestions[this.previousQuestions.length - 1].correct = correct;

    if (correct) {
      await this.setStateAsync({ correct: true });
      if (this.audioEnabled) {
        this.playSound(true);
      }
      this.score += 1;
    } else {
      await this.setStateAsync({ incorrect: true });
      if (this.audioEnabled) {
        this.playSound(false);
      }

    }

    // Reset the state then get a new question
    setTimeout(() => {
      this.setState({
        answer: '',
        formation: dice_formations[0],
        question: '',
        hint: '',
        correct: false,
        incorrect: false,
        colour: colours.red,
      }, () => { this.getQuestion(this.name); });
    }, 400);
  }

  toggleHint() {
    this.setState((state) => ({
      hintVisible: !state.hintVisible
    }));
  }

  setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }

}

Subitising.propTypes = {
  route: PropTypes.object,
  navigation: PropTypes.object
};
